import React, {Component} from 'react';
import {ElementGroup, Direction} from '@pb/kit-component-element-group';
// @ts-expect-error
import {isElementInViewPort} from '@pb/kit-util-is-element-in-view-port';
import {scrollToDomNode} from '@pb/kit-util-smooth-scroll';

import {PaginationStripe} from '../pagination-stripe/pagination-stripe.component';
import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE} from './constants';
import {sliceDataByParams} from './utils/sliceDataByParams';

import type {ReactNode} from 'react';
import type {GroupProps} from '@pb/kit-component-element-group';

import type {PaginationStripeProps} from '../pagination-stripe/pagination-stripe.component';
import type {ClientPaginationType} from './typedef';


export type Props<T> = {
    data: Array<T>,
    onChangePaginationSize?: PaginationStripeProps['onChangePaginationSize'],
    onPaginationChange?: (args: ClientPaginationType) => void,
    children?: (args: Array<T>) => ReactNode,
    pageSize: number,
    currentPage?: number,
    paginationStripeProps?: PaginationStripeProps,
    rootProps?: Partial<GroupProps>,
};

type State = {
    currentPage: number,
    pageSize: number,
};

export class ClientPaginationData<T> extends Component<Props<T>, State> {
    container: HTMLDivElement;

    static defaultProps = {
        currentPage: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
    };

    state = {
        currentPage: this.props.currentPage || DEFAULT_PAGE,
        pageSize: this.props.pageSize || DEFAULT_PAGE_SIZE,
    };

    componentDidUpdate({data}: Props<T>, prevState: State) {
        this.resetOnDataUpdate(data);
        this.onPaginationChange(prevState);
    }

    resetOnDataUpdate(prevData: Array<T>) {
        if (prevData.length !== this.props.data.length) {
            this.setState({currentPage: DEFAULT_PAGE});
        }
    }

    onPaginationChange = ({currentPage: prevPage, pageSize: prevSize}: State) => {
        const {currentPage, pageSize} = this.state;
        if (this.props.onPaginationChange && (prevPage !== currentPage || prevSize !== pageSize)) {
            this.props.onPaginationChange({page: currentPage, pageSize});
        }
    };

    scrollToTopOfSection = () => {
        if (this.container && !isElementInViewPort(this.container)) {
            scrollToDomNode({
                node: this.container,
            });
        }
    };

    onChangePagination = (pageNumber: number, pageSize: number) => {
        this.setState({
            currentPage: pageNumber,
            pageSize,
        }, () => {
            this.scrollToTopOfSection();
        });
    };

    onChangePaginationSize = (currentPage: number, pageSize: number) => {
        if (this.props.onChangePaginationSize) {
            this.props.onChangePaginationSize(currentPage, pageSize);
        }
    };

    prepareData(): Array<T> {
        const {data} = this.props;
        const {currentPage, pageSize} = this.state;

        return sliceDataByParams<T>({
            data,
            pageNumber: currentPage,
            pageSize,
        });
    }

    getRef = (el: HTMLDivElement | null) => {
        if (el) {
            this.container = el;
        }
    };

    render() {
        const {paginationStripeProps, children, data, rootProps} = this.props;
        const {currentPage, pageSize} = this.state;

        return (
            <ElementGroup
                {...rootProps}
                direction={Direction.VERTICAL}
                getRef={this.getRef}>
                {children ? children(this.prepareData()) : null}
                <PaginationStripe
                    {...paginationStripeProps}
                    totalCount={data.length}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onChange={this.onChangePagination}
                    onChangePaginationSize={this.onChangePaginationSize}
                />
            </ElementGroup>
        );
    }
}
